// frontend/src/charts/ChartView.jsx
// The one component that shows a chart in the app: the builder's live
// preview (pass `spec`), a saved chart (pass `chartId`), and later
// dashboard tiles. Fetches through the semantic API, then draws a canvas
// chart, a KPI card or a table. Every failure is a readable message;
// raw server or database text is never shown. `presentation` (7b-2b) sets
// the number format and value labels; it never refetches data. `tableSort`
// and `onTableSort` (7d-2) let a table's headings show and change the sort;
// `onTableResize` (7d-3) lets their edges set a column width. A matrix (7e-2)
// is drawn by MatrixView from the same rows; `matrix` is its layout
// ({ rows, columns, totals }, the spec's `matrix`), which the server does not
// echo. A preview (`spec`) keeps the layout of the spec its rows were fetched
// for (7e-3), so while new data loads the old rows are never read with the
// new layout; a saved chart (`chartId`) passes `matrix`.
//
// Phase 4 step 14b-2: a dashboard tile passes `filters` (the filter bar's
// conditions for this chart, filterState.requestFilters); the saved chart is
// then fetched narrowed (POST .../data). A change of filters waits 250 ms so
// a burst of clicks is one request, and every newer request aborts the one
// before it, so a slow, older reply can never replace a newer one (the
// effect's own AbortController is the version check). `onNarrowing(report,
// filtersKey)` gets the server's report of the filters that did not fit this
// chart, with the JSON of the filters it answers. `filters` null
// (every other caller) fetches exactly as before.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { chartData, chartDataFiltered, renderDraft } from './semanticApi';
import { buildOption, CANVAS_TYPES } from './echartsOption';
import { readTheme } from './palette';
import ChartCanvas from './ChartCanvas';
import KpiCard from './KpiCard';
import DataTable from './DataTable';
import MatrixView from './MatrixView';

const DRAFT_DEBOUNCE_MS = 300;
export const FILTER_DEBOUNCE_MS = 250;

const MESSAGES = {
  LEGACY_CHART: 'This chart was made with the previous chart builder. Open it in the builder to update it.',
  DATASET_UNAVAILABLE: "This chart's dataset is no longer available.",
  INVALID_QUERY: 'A field this chart uses has changed. Edit the chart to update it.',
  INVALID_SPEC: 'This chart needs to be updated before it can display data.',
  INVALID_FILTER: "A dashboard filter couldn't be applied to this chart.",
  ENGINE_NOT_CONFIGURED: "The chart engine isn't configured.",
  QUERY_FAILED: "The data for this chart couldn't be loaded.",
  NOT_FOUND: 'This chart could not be found.',
};

function messageFor(err) {
  return MESSAGES[err && err.code] || (err && err.message) || "This chart couldn't load.";
}

function Note({ children, height, action }) {
  return (
    <div style={{ height }} className="flex flex-col items-center justify-center gap-3 px-6 text-center">
      <p className="text-sm text-[color:var(--chart-text-secondary)]">{children}</p>
      {action}
    </div>
  );
}

export default function ChartView({
  chartId = null, spec = null, type = 'bar', height = 320, showTableToggle = true, presentation = null,
  tableSort = null, onTableSort = null, onTableResize = null, matrix = null, filters = null, onNarrowing = null,
}) {
  const [state, setState] = useState({ status: 'loading', data: null, error: null });
  const [asTable, setAsTable] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const specKey = spec ? JSON.stringify(spec) : null;
  const filtersKey = chartId && Array.isArray(filters) ? JSON.stringify(filters) : null;
  const fetched = useRef(null);            // { chartId, filtersKey } last asked for: only a change of filters on the same chart waits
  const narrowingRef = useRef(onNarrowing);
  narrowingRef.current = onNarrowing;

  useEffect(() => {
    if (!chartId && !specKey) { setState({ status: 'idle', data: null, error: null }); return undefined; }
    const ctrl = new AbortController();
    setState((s) => ({ ...s, status: 'loading', error: null }));
    const run = () => {
      if (!chartId) return renderDraft(JSON.parse(specKey), { signal: ctrl.signal });
      if (filtersKey !== null) return chartDataFiltered(chartId, JSON.parse(filtersKey), { signal: ctrl.signal });
      return chartData(chartId, { signal: ctrl.signal });
    };
    const last = fetched.current;
    const refilter = Boolean(chartId && filtersKey !== null && last && last.chartId === chartId && last.filtersKey !== filtersKey);
    fetched.current = { chartId, filtersKey };
    const timer = setTimeout(() => {
      run()
        .then((data) => {
          if (ctrl.signal.aborted) return;
          const layout = specKey ? JSON.parse(specKey).matrix || null : null;
          setState({ status: 'ready', data, error: null, layout });
          if (narrowingRef.current) narrowingRef.current((data && data.narrowing) || null, filtersKey);
        })
        .catch((error) => {
          if (ctrl.signal.aborted || (error && error.name === 'AbortError')) return;
          setState({ status: 'error', data: null, error });
          if (narrowingRef.current) narrowingRef.current(null, filtersKey);
        });
    }, !chartId ? DRAFT_DEBOUNCE_MS : refilter ? FILTER_DEBOUNCE_MS : 0);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [chartId, specKey, filtersKey, attempt]);

  const data = state.data;
  const presentationKey = presentation ? JSON.stringify(presentation) : '';
  const built = useMemo(() => {
    if (!data || !CANVAS_TYPES.includes(type)) return null;
    return buildOption({ chart: data.chart, type, theme: readTheme(), presentation, columns: data.columns });
    // presentationKey stands in for the object, so a new object with the same values does not redraw.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, type, presentationKey]);

  if (state.status === 'idle') return <Note height={height}>Pick a dataset and a measure to see a preview.</Note>;
  if (state.status === 'loading' && !data) {
    return <div style={{ height }} className="w-full animate-pulse rounded-lg bg-[color:var(--chart-grid)] opacity-40" aria-busy="true" />;
  }
  if (state.status === 'error') {
    const retry = ['QUERY_FAILED', 'HTTP_ERROR'].includes(state.error && state.error.code) || !(state.error && state.error.code);
    return (
      <Note height={height} action={retry ? (
        <button type="button" onClick={() => setAttempt((n) => n + 1)}
          className="text-xs underline text-[color:var(--chart-text-secondary)]">Try again</button>
      ) : null}>
        {messageFor(state.error)}
      </Note>
    );
  }
  if (!data || data.row_count === 0) return <Note height={height}>No data matches this chart's filters.</Note>;

  const layout = specKey ? state.layout : matrix;
  // Matrix chosen but these rows were fetched for another layout: wait for the new ones.
  if (type === 'matrix' && !layout) {
    return <div style={{ height }} className="w-full animate-pulse rounded-lg bg-[color:var(--chart-grid)] opacity-40" aria-busy="true" />;
  }
  const isMatrix = type === 'matrix';
  const table = !isMatrix && (type === 'table' || asTable);
  const notes = [];
  if (data.truncated) notes.push(`Showing the first ${data.row_count.toLocaleString()} rows.`);
  if (!table && built && built.hiddenSeries > 0) {
    notes.push(`Showing 8 of ${data.chart.series.length} series. Add a filter to narrow it down.`);
  }
  const bodyHeight = height - 28;   // the footer row below is always 28px (h-7)

  return (
    <div className={state.status === 'loading' ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
      {table && (
        <DataTable columns={data.columns} rows={data.rows} height={bodyHeight} presentation={presentation}
          sort={tableSort} onSort={onTableSort} onResize={onTableResize} />
      )}
      {isMatrix && (
        <MatrixView columns={data.columns} rows={data.rows} layout={layout} height={bodyHeight} presentation={presentation}
          onResize={onTableResize} />
      )}
      {!table && !isMatrix && type === 'card' && <KpiCard chart={data.chart} height={bodyHeight} presentation={presentation} columns={data.columns} />}
      {!table && !isMatrix && built && <ChartCanvas option={built.option} height={bodyHeight} />}
      {!table && !isMatrix && type !== 'card' && !built && <Note height={bodyHeight}>This chart type isn't available yet.</Note>}
      <div className="flex h-7 items-center justify-between gap-3 text-xs text-[color:var(--chart-text-muted)]">
        <span>{notes.join(' ')}</span>
        {showTableToggle && type !== 'table' && type !== 'card' && !isMatrix && (
          <button type="button" onClick={() => setAsTable((v) => !v)} className="underline">
            {asTable ? 'Show chart' : 'Show table'}
          </button>
        )}
      </div>
    </div>
  );
}
